import React from 'react';
import Decimal from 'decimal.js';

import { FormattedMessage, intlShape } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { types as sdkTypes } from '../../util/sdkLoader';
import {
  propTypes,
  LINE_ITEM_CUSTOMER_COMMISSION,
  LINE_ITEM_PROVIDER_COMMISSION,
} from '../../util/types';
import { isTaxLineItem } from './LineItemTaxWithheldMaybe';

import css from './OrderBreakdown.module.css';

const { Money } = sdkTypes;

const lineItemsTotal = (lineItems, marketplaceCurrency) => {
  const amount = lineItems.reduce((total, item) => {
    return total.plus(Math.abs(item.lineTotal.amount));
  }, new Decimal(0));
  const currency = lineItems[0] ? lineItems[0].lineTotal.currency : marketplaceCurrency;
  return new Money(amount.toNumber(), currency);
};

const isCommission = lineItem =>
  lineItem.code === LINE_ITEM_PROVIDER_COMMISSION ||
  lineItem.code === LINE_ITEM_CUSTOMER_COMMISSION;

const hasCommission = (lineItems, userRole) => {
  if (userRole === 'customer') {
    return !!lineItems.find(item => item.code === LINE_ITEM_CUSTOMER_COMMISSION);
  } else if (userRole === 'provider') {
    return !!lineItems.find(item => item.code === LINE_ITEM_PROVIDER_COMMISSION);
  }
  return false;
};

/**
 * Renders the subtotal line item.
 *
 * Provider subtotal = base + shipping + tax (tax from allLineItems since it's customer-only).
 * Customer subtotal = base + shipping (tax shown separately).
 *
 * @component
 * @param {Object} props
 * @param {Array} props.lineItems - Role-filtered line items
 * @param {Array} props.allLineItems - All unfiltered line items (used for provider tax)
 * @param {string} props.code
 * @param {string} props.userRole
 * @param {intlShape} props.intl
 * @param {string} props.marketplaceCurrency
 */
const LineItemSubTotalMaybe = props => {
  const { lineItems, allLineItems, code, userRole, intl, marketplaceCurrency } = props;

  const refund = lineItems.find(item => item.code === code && item.reversal);
  const showSubTotal = hasCommission(lineItems, userRole) || refund;

  const isProviderRole = userRole === 'provider';

  // Base items: non-commission, non-tax, non-reversal from role-filtered lineItems
  const baseItems = lineItems.filter(
    item => !isCommission(item) && !isTaxLineItem(item) && !item.reversal
  );

  // For provider: add tax items from allLineItems (tax is customer-only on server)
  const taxItems = isProviderRole
    ? (allLineItems || []).filter(item => isTaxLineItem(item) && !item.reversal)
    : [];

  const subTotalLineItems = [...baseItems, ...taxItems];
  const subTotal = subTotalLineItems.length > 0
    ? lineItemsTotal(subTotalLineItems, marketplaceCurrency)
    : null;

  const formattedSubTotal = subTotal ? formatMoney(intl, subTotal) : null;

  return formattedSubTotal && showSubTotal ? (
    <>
      <hr className={css.totalDivider} />
      <div className={css.subTotalLineItem}>
        <span className={css.itemLabel}>
          <FormattedMessage id="OrderBreakdown.subTotal" />
        </span>
        <span className={css.itemValue}>{formattedSubTotal}</span>
      </div>
    </>
  ) : null;
};

export default LineItemSubTotalMaybe;
